// Shared trip dates and time helper utilities

export const TRIP_START_DATE = "2027-01-07";
export const TRIP_END_DATE = "2027-01-14";

export const TRIP_DATES = {
  "Thursday 1/7": "2027-01-07",
  "Friday 1/8": "2027-01-08",
  "Saturday 1/9": "2027-01-09",
  "Sunday 1/10": "2027-01-10",
  "Monday 1/11": "2027-01-11",
  "Tuesday 1/12": "2027-01-12",
  "Wednesday 1/13": "2027-01-13",
  "Thursday 1/14": "2027-01-14"
};

export const DAYS = [
  "Thursday 1/7",
  "Friday 1/8",
  "Saturday 1/9",
  "Sunday 1/10",
  "Monday 1/11",
  "Tuesday 1/12",
  "Wednesday 1/13",
  "Thursday 1/14"
];

/**
 * Computes where "today" falls relative to the trip window, for the dashboard's
 * vacation countdown banner: a day count while it's still upcoming, "Vacation Mode"
 * once the trip window has started, and nothing once it's over.
 *
 * @returns {{ daysUntil: number, isVacationWeek: boolean, isPastTrip: boolean }}
 */
export const getVacationCountdown = () => {
  const parseLocalDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = parseLocalDate(TRIP_START_DATE);
  const end = parseLocalDate(TRIP_END_DATE);

  const isVacationWeek = today >= start && today <= end;
  const isPastTrip = today > end;
  const daysUntil = Math.ceil((start - today) / (1000 * 60 * 60 * 24));

  return { daysUntil, isVacationWeek, isPastTrip };
};

/**
 * Resolves a date string (either YYYY-MM-DD or textual date) to a standard trip day label.
 * 
 * @param {string} dateStr The date string to convert
 * @returns {string} The matched trip day label, or empty string if not found
 */
export const dateToTripDay = (dateStr) => {
  if (!dateStr) return "";
  
  // 1. Try exact key matching first if it's dynamic format like YYYY-MM-DD
  const found = Object.entries(TRIP_DATES).find(([_, date]) => date === dateStr);
  if (found) return found[0];
  
  // 2. Check if the string matches one of the day keys directly
  if (TRIP_DATES[dateStr]) return dateStr;
  
  // 3. Fallback to substring matching for flexible user input
  const lower = dateStr.toLowerCase();
  if (lower.includes("1/10") || lower.includes("01-10") || lower.includes("jan 10")) return "Sunday 1/10";
  if (lower.includes("1/11") || lower.includes("01-11") || lower.includes("jan 11")) return "Monday 1/11";
  if (lower.includes("1/12") || lower.includes("01-12") || lower.includes("jan 12")) return "Tuesday 1/12";
  if (lower.includes("1/13") || lower.includes("01-13") || lower.includes("jan 13")) return "Wednesday 1/13";
  if (lower.includes("1/14") || lower.includes("01-14") || lower.includes("jan 14")) return "Thursday 1/14";
  if (lower.includes("1/7") || lower.includes("01-07") || lower.includes("jan 7")) return "Thursday 1/7";
  if (lower.includes("1/8") || lower.includes("01-08") || lower.includes("jan 8")) return "Friday 1/8";
  if (lower.includes("1/9") || lower.includes("01-09") || lower.includes("jan 9")) return "Saturday 1/9";

  return "";
};

/**
 * Parses a raw date and/or time string and returns parsed structured components.
 * Formats time string to standard 12-hour AM/PM.
 * 
 * @param {string} rawTimeStr Raw datetime string (e.g. "14:55, Jul 16", "2027-01-07T14:55")
 * @returns {object} { time: string, date: string }
 */
export const parseTimeAndDate = (rawTimeStr) => {
  if (!rawTimeStr) return { time: "TBD", date: "" };

  let timePart = "";
  let datePart = "";

  const trimmed = rawTimeStr.trim();

  // Case 1: Comma separated (e.g., "14:55, Jul 16" or "2:55 PM, Jun 13")
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',').map(s => s.trim());
    if (parts[0].includes(':')) {
      timePart = parts[0];
      datePart = parts[1] || "";
    } else if (parts[1] && parts[1].includes(':')) {
      timePart = parts[1];
      datePart = parts[0];
    }
  }

  // Case 2: ISO style YYYY-MM-DD[T ]HH:MM
  if (!timePart) {
    const isoMatch = trimmed.match(/(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{2})(?::\d{2})?/);
    if (isoMatch) {
      datePart = isoMatch[1];
      timePart = `${isoMatch[2]}:${isoMatch[3]}`;
    }
  }

  // Case 3: Fallback regex to find time HH:MM (AM/PM)
  if (!timePart) {
    const timeMatch = trimmed.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i);
    if (timeMatch) {
      timePart = timeMatch[0];
      datePart = trimmed.replace(timeMatch[0], "").replace(/^[TZ\s,:]+|[TZ\s,:]+$/g, "").trim();
    }
  }

  if (!timePart) {
    return { time: trimmed, date: "" };
  }

  // Format to 12-hour AM/PM
  let formattedTime = timePart;
  if (!timePart.toLowerCase().includes('am') && !timePart.toLowerCase().includes('pm')) {
    const match = timePart.match(/(\d{1,2}):(\d{2})/);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      formattedTime = `${hours}:${minutes} ${ampm}`;
    }
  }

  return { time: formattedTime, date: datePart };
};
